import React from 'react';
import { Code, Database, Wrench, Palette, Terminal } from 'lucide-react';

const Skills = () => {
  const skillCategories = [
    {
      title: "Programming Languages",
      icon: <Code className="w-6 h-6" />,
      color: "from-cyan-400 to-blue-500",
      skills: ["C++", "Python", "JavaScript", "SQL", "Ruby", "Shell Scripting"]
    },
    {
      title: "Web Development",
      icon: <Database className="w-6 h-6" />,
      color: "from-purple-400 to-pink-500",
      skills: ["ReactJS", "Node.js", "Express.js", "MongoDB", "HTML", "CSS", "Fetch API"]
    },
    {
      title: "Tools & Platforms", 
      icon: <Wrench className="w-6 h-6" />,
      color: "from-green-400 to-cyan-500",
      skills: ["Git", "GitHub", "VS Code", "Visual Studio", "SQL Server"]
    },
    {
      title: "Libraries & Frameworks",
      icon: <Terminal className="w-6 h-6" />,
      color: "from-orange-400 to-red-500",
      skills: ["STL", "pandas", "NumPy", "Matplotlib"]
    },
    {
      title: "Design & Styling",
      icon: <Palette className="w-6 h-6" />,
      color: "from-pink-400 to-purple-500",
      skills: ["TailwindCSS", "Responsive Design", "UI/UX"]
    }
  ];

  return (
    <section id="skills" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Technical <span className="text-cyan-400">Skills</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Technologies and tools I use to bring ideas to life
            </p>
          </div>

          {/* Skills Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skillCategories.map((category, index) => (
              <div 
                key={index}
                className="group bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:transform hover:scale-105"
              >
                {/* Category Header */}
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${category.color} mr-4 group-hover:scale-110 transition-transform duration-300`}>
                    <div className="text-white">
                      {category.icon}
                    </div>
                  </div>
                  <h3 className="text-white font-semibold text-lg">
                    {category.title}
                  </h3>
                </div>

                {/* Skills List */}
                <div className="space-y-3">
                  {category.skills.map((skill, skillIndex) => (
                    <div 
                      key={skillIndex}
                      className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 transition-colors duration-300"
                    >
                      <span className="text-gray-300 font-medium">{skill}</span>
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, i) => {
                          let rating = 4; // Default rating
                          
                          // Set specific ratings for certain skills
                          if (skill === 'JavaScript' || skill === 'HTML' || skill === 'CSS' || skill === 'Python' || skill === 'SQL') {
                            rating = 4;
                          } else if (skill === 'C++') {
                            rating = 5;
                          } else if (skill === 'MongoDB' || skill === 'Express.js' || skill === 'ReactJS' || skill === 'Node.js') {
                            rating = 4;
                          } else {
                            rating = 3;
                          }
                          
                          return (
                            <div 
                              key={i}
                              className={`w-2 h-2 rounded-full ${
                                i < rating 
                                  ? `bg-gradient-to-r ${category.color}` 
                                  : 'bg-gray-600'
                              }`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Tech Stack Showcase */}
          <div className="mt-16 p-8 bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm rounded-2xl border border-gray-600">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              <span className="text-cyan-400">MERN</span> Stack Specialist
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-green-400 font-bold text-xl">M</span>
                </div>
                <h4 className="text-white font-semibold">MongoDB</h4>
                <p className="text-gray-400 text-sm">Database</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-yellow-400 font-bold text-xl">E</span>
                </div>
                <h4 className="text-white font-semibold">Express.js</h4>
                <p className="text-gray-400 text-sm">Backend</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-blue-400 font-bold text-xl">R</span>
                </div>
                <h4 className="text-white font-semibold">ReactJS</h4>
                <p className="text-gray-400 text-sm">Frontend</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-green-400 font-bold text-xl">N</span>
                </div>
                <h4 className="text-white font-semibold">Node.js</h4>
                <p className="text-gray-400 text-sm">Runtime</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
